import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { SignalTextarea, SignalTextField } from '@vallift/ngl';
import type { FormPage } from '../../models/built-form';
import { DEFAULT_PRIMARY_LABEL } from '../../utils/form-pages';

interface PageStepDraft {
  title: string;
  description: string;
  primaryLabel: string;
  backLabel: string;
}

@Component({
  selector: 'app-form-page-step-config-panel',
  standalone: true,
  imports: [SignalTextField, SignalTextarea],
  templateUrl: './form-page-step-config-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormPageStepConfigPanelComponent {
  readonly page = input<FormPage | null>(null);
  readonly canDelete = input(false);
  readonly pageChange = output<FormPage>();
  readonly deletePage = output<void>();

  readonly draft = signal<PageStepDraft>({
    title: '',
    description: '',
    primaryLabel: DEFAULT_PRIMARY_LABEL,
    backLabel: '',
  });

  private skipNextSync = false;

  constructor() {
    effect(() => {
      const next = this.page();
      if (this.skipNextSync) {
        this.skipNextSync = false;
        return;
      }
      if (!next) {
        this.draft.set({
          title: '',
          description: '',
          primaryLabel: DEFAULT_PRIMARY_LABEL,
          backLabel: '',
        });
        return;
      }
      this.draft.set({
        title: next.title ?? '',
        description: next.description ?? '',
        primaryLabel: next.cta?.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL,
        backLabel: next.cta?.backLabel ?? '',
      });
    });
  }

  updateDraft<K extends keyof PageStepDraft>(key: K, value: PageStepDraft[K]): void {
    this.draft.update((current) => ({ ...current, [key]: value }));
    this.emitChange();
  }

  onDelete(): void {
    if (this.canDelete()) {
      this.deletePage.emit();
    }
  }

  private emitChange(): void {
    const current = this.page();
    if (!current) {
      return;
    }
    const draft = this.draft();
    const backLabel = draft.backLabel.trim();
    this.skipNextSync = true;
    this.pageChange.emit({
      ...current,
      title: draft.title.trim() || undefined,
      description: draft.description.trim() || undefined,
      cta: {
        primaryLabel: draft.primaryLabel.trim() || DEFAULT_PRIMARY_LABEL,
        ...(current.order > 1 && backLabel ? { backLabel } : {}),
      },
    });
  }
}
